import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Brain, Target, ChartLine, Lightning, GraduationCap } from "@phosphor-icons/react";
import { useT } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/app/LanguageSwitcher";
import Footer from "../components/app/Footer";
import SEOHead from "../components/app/SEOHead";
import { usePlans } from "../lib/usePlans";

export default function Landing() {
  const { t, lang } = useT();
  const features = [
    { icon: Brain, title: t("landing.feature.ai.title"), body: t("landing.feature.ai.body"), span: "lg:col-span-7" },
    { icon: Target, title: t("landing.feature.score.title"), body: t("landing.feature.score.body"), span: "lg:col-span-5" },
    { icon: ChartLine, title: t("landing.feature.pipeline.title"), body: t("landing.feature.pipeline.body"), span: "lg:col-span-5" },
    { icon: Lightning, title: t("landing.feature.bank.title"), body: t("landing.feature.bank.body"), span: "lg:col-span-7" },
  ];
  const { plans: dbPlans } = usePlans();
  const monthlyPlan = dbPlans.find((p) => p.plan_id === "monthly");
  const tiers = dbPlans.length ? dbPlans.map((p) => {
    const period = p.days === 30
      ? (lang === "tr" ? "/ ay" : "/ month")
      : p.days >= 360
        ? (lang === "tr" ? "/ yıl" : "/ year")
        : (lang === "tr" ? `/ ${Math.round(p.days / 30)} ay` : `/ ${Math.round(p.days / 30)} months`);
    let savingsPct = 0;
    if (monthlyPlan && p.days > 30) {
      const monthlyEquiv = p.price_total / (p.days / 30);
      savingsPct = Math.round((1 - monthlyEquiv / monthlyPlan.price_total) * 100);
    }
    return {
      name: lang === "tr" ? p.name_tr : p.name_en,
      price: `${p.price_total.toLocaleString(lang === "tr" ? "tr-TR" : "en-US")} ₺`,
      period,
      desc: savingsPct > 0
        ? (lang === "tr" ? `Aylıktan %${savingsPct} tasarruf.` : `Save ${savingsPct}% vs monthly.`)
        : (lang === "tr" ? "Aylık esneklik istiyorsan." : "If you want monthly flexibility."),
      features: lang === "tr" ? (p.features_tr || []) : (p.features_en || []),
      cta: lang === "tr" ? "Başla" : "Get started",
      featured: p.featured,
    };
  }) : [];

  // Rich structured data — Course + Product schemas boost SEO with rich results
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Course",
      name: lang === "tr" ? "PMP Sınav Hazırlığı — AI Koçlu" : "PMP Exam Prep — AI-Coached",
      description: lang === "tr"
        ? "PMBOK 8 alanları üzerinden yapay zeka koçluğuyla, 1700+ soruyla PMP sınavına hazırlanın."
        : "Prepare for the PMP exam with 1,700+ questions and AI coaching across PMBOK 8 domains.",
      provider: {
        "@type": "Organization",
        name: "PMCoach AI",
        sameAs: "https://pmcoach-ai.com",
      },
      inLanguage: [lang === "tr" ? "tr" : "en"],
      educationalLevel: "Professional",
      teaches: "PMBOK 8 — People, Process, Business Environment",
    },
    ...(dbPlans.length ? [{
      "@context": "https://schema.org",
      "@type": "Product",
      name: "PMCoach AI — PMP Prep Subscription",
      description: lang === "tr"
        ? "PMBOK 8 içerikli, AI koçlu, çift dilli (TR/EN) PMP sınav hazırlık aboneliği."
        : "AI-coached, bilingual (TR/EN) PMP exam prep subscription with PMBOK 8 content.",
      brand: { "@type": "Brand", name: "PMCoach AI" },
      offers: dbPlans.filter(p => p.plan_id !== "free").map(p => ({
        "@type": "Offer",
        name: lang === "tr" ? p.name_tr : p.name_en,
        price: p.price_total.toFixed(2),
        priceCurrency: "TRY",
        availability: "https://schema.org/InStock",
        url: "https://pmcoach-ai.com/register",
      })),
    }] : []),
  ];

  return (
    <div className="min-h-screen">
      <SEOHead pageKey="landing" jsonLd={jsonLd} />
      <ReferralCapture />
      <header className="glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo-full.png" alt="PMCoach AI" className="h-12 w-auto" data-testid="brand-logo" />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-[#002FA7] hidden sm:inline" data-testid="header-login">{t("common.login")}</Link>
            <Link to="/register" className="btn-primary !py-2 !px-4 text-sm" data-testid="header-signup">{t("landing.cta.primary")}</Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <div className="overline text-[#002FA7]">{t("landing.overline")}</div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[0.95] mt-4"
            >
              {t("landing.h1.line1")} <br />
              <span className="text-[#002FA7]">{t("landing.h1.line2")}</span> {t("landing.h1.line3")}
            </motion.h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">{t("landing.subtitle")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
                {t("landing.cta.primary")} <span aria-hidden>→</span>
              </Link>
              <Link to="/login" className="btn-secondary" data-testid="hero-cta-secondary">{t("landing.cta.secondary")}</Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1"><CheckCircle size={16} weight="fill" className="text-[#059669]" /> {t("landing.bullets.1")}</span>
              <span className="flex items-center gap-1"><CheckCircle size={16} weight="fill" className="text-[#059669]" /> {t("landing.bullets.2")}</span>
              <span className="flex items-center gap-1"><CheckCircle size={16} weight="fill" className="text-[#059669]" /> {t("landing.bullets.3")}</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="card p-2 shadow-sm">
              <img
                src="https://images.unsplash.com/photo-1758876201985-66c20343e681?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="PMP study"
                className="rounded-md w-full object-cover h-[420px]"
              />
            </div>
            <div className="card absolute -left-4 -bottom-6 hidden md:block w-72 shadow-md" data-testid="hero-readiness-card">
              <div className="overline">{t("landing.readiness.label")}</div>
              <div className="font-display text-4xl font-bold mt-1">82<span className="text-slate-400 text-2xl">/100</span></div>
              <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#002FA7]" style={{ width: "82%" }} />
              </div>
              <p className="text-xs text-slate-500 mt-2">{t("landing.readiness.note")} <span className="text-[#E11D48] font-semibold">Process</span></p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="overline text-[#002FA7]">{t("landing.features.overline")}</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">{t("landing.features.title")}</h2>
          <p className="text-slate-600 mt-3 max-w-2xl">{t("landing.features.subtitle")}</p>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {features.map((f, i) => (
              <div key={i} className={`card card-hover ${f.span}`} data-testid={`feature-card-${i}`}>
                <div className="h-10 w-10 rounded-md bg-[#DBEAFE] grid place-items-center">
                  <f.icon size={22} weight="duotone" className="text-[#002FA7]" />
                </div>
                <h3 className="font-display text-xl font-semibold mt-4">{f.title}</h3>
                <p className="text-slate-600 mt-2">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="border-t border-slate-200 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="overline text-[#002FA7]">{t("landing.pricing.overline")}</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">{t("landing.pricing.title")}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`card card-hover relative ${tier.featured ? "border-t-4 border-t-[#002FA7] shadow-md" : ""}`}
                data-testid={`tier-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {tier.featured && <div className="overline text-[#002FA7] absolute -top-3 left-6 bg-white px-2">{t("landing.tier.popular")}</div>}
                <h3 className="font-display text-2xl font-semibold">{tier.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold">{tier.price}</span>
                  <span className="text-sm text-slate-500">{tier.period}</span>
                </div>
                <p className="text-slate-600 mt-2 text-sm">{tier.desc}</p>
                <ul className="mt-6 space-y-2 text-sm">
                  {tier.features.map((feat, k) => (
                    <li key={k} className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#059669] mt-0.5" /> {feat}</li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`mt-6 block text-center ${tier.featured ? "btn-primary" : "btn-secondary"}`}
                  data-testid={`tier-cta-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {tier.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/**
 * Detects ?ref=CODE on landing URL and persists it to localStorage so that the
 * Register flow can attach it to the user. Renders nothing.
 */
function ReferralCapture() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref");
    if (code && code.length >= 4 && code.length <= 20) {
      localStorage.setItem("pmpintel_ref_code", code.toUpperCase());
      // Best-effort click tracking (unauthenticated)
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      if (apiBase) {
        fetch(`${apiBase}/api/affiliate/track/${encodeURIComponent(code)}`, { method: "GET" }).catch(() => { /* noop */ });
      }
    }
  } catch { /* noop */ }
  return null;
}
